import React from "react";

import {
  Box,
  Grid,
  Typography,
  makeStyles,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { useFeature } from "flagged";

import { CalendarSummary, News, TodaysThingsToDo } from "@hubcapp/components";
import Content from "@ds-developer/content";
import PageHeader from "@ds-developer/page-header";

import { ArrearsCard } from "./components";
import {
  baseSettings,
  defaultCardStyles,
  getTitleForCurrentTime,
  withArrearsCardStyles,
} from "./helpers";
import { useAuth } from "@hubcapp/context";
import { useQueryInstructorArrears } from "@hubcapp/hooks";

const useStyles = (arrearsCardShouldShow: boolean) =>
  arrearsCardShouldShow
    ? makeStyles(theme => ({
        ...baseSettings(theme),
        ...withArrearsCardStyles(theme),
      }))
    : makeStyles(theme => ({
        ...baseSettings(theme),
        ...defaultCardStyles(theme),
      }));

const Home = () => {
  const {
    instructor: { firstName, id },
  } = useAuth();

  const {
    loading: arrearsLoading,
    data: arrearsData,
  } = useQueryInstructorArrears(id);

  let sumOfAllArrears = 0;
  if (arrearsData?.instructor.arrears) {
    sumOfAllArrears = arrearsData.instructor.arrears.reduce(
      (acc, { amount }) => acc + amount,
      0,
    );
  }

  const arrearsCardShouldShow =
    useFeature("instructorArrearsEnabled") && sumOfAllArrears > 0;

  const theme = useTheme();
  const classes = useStyles(arrearsCardShouldShow)();
  const isXLScreen = useMediaQuery(theme.breakpoints.up("xl"), {
    defaultMatches: true,
  });

  return (
    <Content>
      <PageHeader
        breadcrumbs={[{ title: "Home" }]}
        title={<span data-happo-hide>{getTitleForCurrentTime(firstName)}</span>}
        subtitle="Here’s what’s happening with your business today."
      />
      <Box paddingTop={2}>
        <Grid container spacing={isXLScreen ? 3 : 5} className={classes.layout}>
          {arrearsCardShouldShow && (
            <Box className={classes.arrears}>
              <Box paddingBottom={2}>
                <Typography
                  component="p"
                  variant="overline"
                  color="textSecondary"
                >
                  Arrears
                </Typography>
              </Box>

              <ArrearsCard
                isLoading={arrearsLoading}
                arrears={sumOfAllArrears}
              />
            </Box>
          )}

          <Box className={classes.news}>
            <Box paddingBottom={2}>
              <Typography
                component="p"
                variant="overline"
                color="textSecondary"
              >
                News
              </Typography>
            </Box>

            <News />
          </Box>

          <Box className={classes.thingsToDo}>
            <Box paddingBottom={2}>
              <Typography
                component="p"
                variant="overline"
                color="textSecondary"
              >
                Today&apos;s things to do
              </Typography>
            </Box>

            <TodaysThingsToDo />
          </Box>

          <Box className={classes.calendar}>
            <Box paddingBottom={2}>
              <Typography
                component="p"
                variant="overline"
                color="textSecondary"
              >
                Today&apos;s lessons
              </Typography>
            </Box>

            <CalendarSummary />
          </Box>
        </Grid>
      </Box>
    </Content>
  );
};
export default Home;
